import React from 'react';
import { Link } from 'react-router-dom';
import { Download, Play, TrendingUp, Users } from 'lucide-react';
import { FeaturedAddins } from './FeaturedAddins';
import { LatestTutorials } from './LatestTutorials';
import { CommunitySection } from './CommunitySection';
import { isExcelAfterDark, siteConfig } from '../config/site';
import { ExcelAfterDarkHome } from './ExcelAfterDarkHome';

export const HomePage = () => {
  if (isExcelAfterDark) return <ExcelAfterDarkHome />;

  return (
  <>
    {/* Hero Section */}
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="text-center">
        <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
          {isExcelAfterDark ? (
            <>Excel gets interesting <span className={siteConfig.theme.accentText}>after dark.</span></>
          ) : (
            <>Excel Solutions That <span className={siteConfig.theme.accentText}>Transform</span> Your World</>
          )}
        </h1>
        <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
          {isExcelAfterDark
            ? 'Original Excel tools, experiments, and unexpected solutions created after hours—including a VBA Colour Picker that sends colour data straight into your work.'
            : 'Discover powerful Excel add-ins, watch expert tutorials, and join a community of Excel enthusiasts.'}
        </p>
        <div className="flex flex-col sm:flex-row justify-center gap-4">
          <Link
            to={isExcelAfterDark ? '/products/excel-colour-picker' : '/products'}
            className={`${siteConfig.theme.button} text-white px-8 py-3 rounded-lg flex items-center justify-center`}
          >
            <Download className="mr-2 h-5 w-5" />
            {isExcelAfterDark ? 'Explore the Colour Picker' : 'Browse Add-ins'}
          </Link>
          <Link
            to={isExcelAfterDark ? '/products/excel-colour-picker' : '/tutorials'}
            className={`bg-white ${siteConfig.theme.buttonText} px-8 py-3 rounded-lg border-2 ${siteConfig.theme.buttonBorder} ${siteConfig.theme.buttonHover} flex items-center justify-center`}
          >
            <Play className="mr-2 h-5 w-5" />
            {isExcelAfterDark ? 'See How It Works' : 'Watch Tutorials'}
          </Link>
        </div>
      </div>
    </section>

    {/* Features Grid */}
    <section className="bg-gray-50 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-white p-6 rounded-xl shadow-sm">
            <TrendingUp className={`h-12 w-12 ${siteConfig.theme.accentText} mb-4`} />
            <h3 className="text-xl font-semibold mb-2">{isExcelAfterDark ? 'Original Tools' : 'Productivity Add-ins'}</h3>
            <p className="text-gray-600">{isExcelAfterDark ? 'Purpose-built Excel creations that solve problems in unexpected ways.' : 'Professional Excel solutions to automate your workflow and boost productivity.'}</p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm">
            <Play className={`h-12 w-12 ${siteConfig.theme.accentText} mb-4`} />
            <h3 className="text-xl font-semibold mb-2">{isExcelAfterDark ? 'Built After Hours' : 'Video Tutorials'}</h3>
            <p className="text-gray-600">{isExcelAfterDark ? 'Ideas developed at night, beyond the limits of the ordinary workday.' : 'Step-by-step guides and expert tutorials to master Excel features.'}</p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm">
            <Users className={`h-12 w-12 ${siteConfig.theme.accentText} mb-4`} />
            <h3 className="text-xl font-semibold mb-2">{isExcelAfterDark ? 'For Curious Excel Users' : 'Community'}</h3>
            <p className="text-gray-600">{isExcelAfterDark ? 'For people who want Excel to do more than the usual formulas and reports.' : 'Connect with Excel experts and enthusiasts to share knowledge and tips.'}</p>
          </div>
        </div>
      </div>
    </section>

    <FeaturedAddins />
    {!isExcelAfterDark && <LatestTutorials />}
    {!isExcelAfterDark && <CommunitySection />}
  </>
  );
};
